import { useCallback, useEffect, useState } from 'react'
import {
  parseExpandedKeys,
  serializeExpandedKeys,
  toggleExpandedKey,
  withExpandedKey,
} from './sidebar-tree.js'

const STORAGE_KEY = 'terafile:sidebar:expanded'

/**
 * localStorage はプライベートブラウズやストレージ無効化で例外を投げることがある。
 * 展開状態は保存できなくても操作自体は成立するので、読み書きの失敗は
 * 「保存しない」として扱い、サイドバーを壊さない。
 */
function readStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStorage(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // 上記のとおり、保存できなくても画面上の開閉はそのまま使える
  }
}

export type SidebarExpanded = {
  expanded: ReadonlySet<string>
  toggle: (key: string) => void
  /** 閲覧中のプロジェクトが入っているフォルダを開くときに使う。閉じる操作はしない */
  ensureExpanded: (key: string) => void
}

/** サイドバーのフォルダツリーの開閉状態。リロードしても維持する */
export function useSidebarExpanded(): SidebarExpanded {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    parseExpandedKeys(readStorage()),
  )

  useEffect(() => {
    writeStorage(serializeExpandedKeys(expanded))
  }, [expanded])

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => toggleExpandedKey(prev, key))
  }, [])

  const ensureExpanded = useCallback((key: string) => {
    setExpanded((prev) => withExpandedKey(prev, key))
  }, [])

  return { expanded, toggle, ensureExpanded }
}
